import { unzipSync } from "fflate"

// Browser-side preview for NIQ Fragrances .xlsx exports. Mirrors the server
// parser (`app/lib/niq_fragrance_file_parser.rb`): it reads only the header row
// of the "1-Table-1" sheet to detect the monthly period columns (position-
// independent, recognized by label), and approximates the data-row count from
// the number of <row> elements after the header — without parsing every cell, so
// it survives large sheets. The server remains the source of truth at import.

const SHEET_NAME = "1-Table-1"

// Rows to scan per sheet when hunting for the BRAND header. Mirrors the server
// parser; the header always sits near the top of a NIQ export.
const MAX_HEADER_SCAN = 200

export type NiqPreviewResult =
  | {
      unknown?: false
      filename: string
      periods: [number, number][] // sorted [year, month]
      rowCount: number // approximate data rows
    }
  | {
      unknown: true
      filename: string
      reason: string
    }

const MONTH_MAP: Record<string, number> = {
  jan: 1, january: 1, januari: 1,
  feb: 2, february: 2, februari: 2,
  mar: 3, march: 3, maret: 3,
  apr: 4, april: 4,
  may: 5, mei: 5,
  jun: 6, june: 6, juni: 6,
  jul: 7, july: 7, juli: 7,
  aug: 8, august: 8, agustus: 8, agu: 8, agt: 8, ags: 8,
  sep: 9, september: 9, sept: 9,
  oct: 10, october: 10, oktober: 10, okt: 10,
  nov: 11, november: 11,
  dec: 12, december: 12, desember: 12, des: 12,
}

// Classify a NIQ period header label. Only monthly labels return a period;
// quarterly ("Q2 23") and YTD labels are intentionally ignored (return null).
export function classifyPeriodLabel(label: string): [number, number] | null {
  const s = (label ?? "").trim()
  if (!s) return null

  // Drop the descriptive " - w/e <date>" suffix before classifying.
  const head = s.split(/\s+-\s+/)[0].trim()

  if (/\bYTD\b/i.test(s) || /^YTD\b/i.test(head)) return null
  if (/^Q[1-4]\b/i.test(head)) return null

  const m = head.match(/^([A-Za-z]{3,})\.?\s+'?(\d{2,4})$/)
  if (m) {
    const month = MONTH_MAP[m[1].toLowerCase()]
    if (month) {
      let year = parseInt(m[2], 10)
      if (year < 100) year += 2000
      return [year, month]
    }
  }
  return null
}

export async function parseNiqFragranceForPreview(file: File): Promise<NiqPreviewResult> {
  const filename = file.name
  if (!filename.toLowerCase().endsWith(".xlsx")) {
    // .xls can't be read client-side here; let the admin tag + import it anyway.
    return {
      unknown: true,
      filename,
      reason: "Preview hanya tersedia untuk .xlsx. File tetap bisa di-import.",
    }
  }

  try {
    const buf = await file.arrayBuffer()
    const data = new Uint8Array(buf)
    const decode = (b: Uint8Array) => new TextDecoder().decode(b)

    const meta = unzipSync(data, {
      filter: (f) => f.name === "xl/workbook.xml" || f.name === "xl/_rels/workbook.xml.rels",
    })
    if (!meta["xl/workbook.xml"]) {
      return { unknown: true, filename, reason: "workbook.xml tidak ditemukan dalam file." }
    }

    const wbXml = decode(meta["xl/workbook.xml"])
    const relsXml = meta["xl/_rels/workbook.xml.rels"] ? decode(meta["xl/_rels/workbook.xml.rels"]) : ""

    // Try the canonical sheet first, then fall back to every other sheet: the
    // right data table is whichever sheet has a "BRAND" header row. Analytical/
    // pivot workbooks have none and are reported as not-importable.
    const candidatePaths = orderedSheetPaths(wbXml, relsXml, SHEET_NAME)
    if (candidatePaths.length === 0) {
      return { unknown: true, filename, reason: "Tidak ada worksheet yang bisa dibaca dari file." }
    }

    const ss = parseSharedStrings(
      unzipSync(data, { filter: (f) => f.name === "xl/sharedStrings.xml" })["xl/sharedStrings.xml"],
      decode,
    )

    for (const wsPath of candidatePaths) {
      const extracted = unzipSync(data, { filter: (f) => f.name === wsPath })
      const wsXml = extracted[wsPath] ? decode(extracted[wsPath]) : ""
      if (!wsXml) continue

      const rowChunks = wsXml.split("<row ")
      // Locate the header row: the first row containing a "BRAND" cell.
      let headerIndex = -1
      let periods: [number, number][] = []
      const scanLimit = Math.min(rowChunks.length, MAX_HEADER_SCAN + 1)
      for (let i = 1; i < scanLimit; i++) {
        const rowXml = "<row " + rowChunks[i]
        const values = rowCellValues(rowXml, ss)
        if (values.some((v) => String(v ?? "").trim().toUpperCase() === "BRAND")) {
          headerIndex = i
          const detected = new Map<string, [number, number]>()
          for (const v of values) {
            const p = classifyPeriodLabel(String(v ?? ""))
            if (p) detected.set(`${p[0]}-${p[1]}`, p)
          }
          periods = [...detected.values()].sort((a, b) => a[0] - b[0] || a[1] - b[1])
          break
        }
      }

      if (headerIndex !== -1) {
        // Approximate data rows = <row> elements after the header.
        const rowCount = Math.max(0, rowChunks.length - 1 - headerIndex)
        return { filename, periods, rowCount }
      }
    }

    return {
      unknown: true,
      filename,
      reason:
        "Tidak ada tabel data NIQ (kolom BRAND) di file ini — sepertinya bukan file ekspor yang bisa di-import.",
    }
  } catch (err) {
    return {
      unknown: true,
      filename,
      reason: err instanceof Error ? err.message : "Gagal membaca file",
    }
  }
}

// ---------------------------------------------------------------------------
// Minimal XLSX helpers (kept local so this module is independent)
// ---------------------------------------------------------------------------

function resolveSheetPath(wbXml: string, relsXml: string, sheetName: string): string | null {
  const escaped = sheetName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
  const sheetTag = wbXml.match(new RegExp(`<sheet\\s[^>]*name="${escaped}"[^>]*>`))
  if (!sheetTag) return null
  const rId = sheetTag[0].match(/r:id="([^"]+)"/)?.[1]
  if (!rId) return null

  return relIdToPath(relsXml, rId)
}

function relIdToPath(relsXml: string, rId: string): string | null {
  const relParts = relsXml.split(/<Relationship\b/)
  for (let i = 1; i < relParts.length; i++) {
    if (relParts[i].includes(`Id="${rId}"`)) {
      const target = relParts[i].match(/\bTarget="([^"]+)"/)?.[1]
      if (target) {
        if (target.startsWith("../")) return target.slice(3)
        if (target.startsWith("/")) return target.slice(1)
        return `xl/${target}`
      }
    }
  }
  return null
}

// All worksheet paths in the workbook, with the preferred sheet (if present)
// moved to the front so the common case is checked first and de-duplicated.
function orderedSheetPaths(wbXml: string, relsXml: string, preferredName: string): string[] {
  const preferred = resolveSheetPath(wbXml, relsXml, preferredName)
  const all: string[] = []
  for (const tag of wbXml.match(/<sheet\s[^>]*>/g) ?? []) {
    const rId = tag.match(/r:id="([^"]+)"/)?.[1]
    if (!rId) continue
    const path = relIdToPath(relsXml, rId)
    if (path) all.push(path)
  }
  const ordered = preferred ? [preferred, ...all.filter((p) => p !== preferred)] : all
  return [...new Set(ordered)]
}

function parseSharedStrings(bytes: Uint8Array | undefined, decode: (b: Uint8Array) => string): string[] {
  if (!bytes) return []
  const xml = decode(bytes)
  const ss: string[] = []
  for (const part of xml.split("<si>").slice(1)) {
    const matches = [...part.matchAll(/<t(?:[^>]*)>(.*?)<\/t>/gs)]
    ss.push(unescapeXml(matches.map((m) => m[1]).join("")))
  }
  return ss
}

// Reads every cell value of a single row, in document order.
function rowCellValues(rowXml: string, ss: string[]): (string | null)[] {
  const endIdx = rowXml.indexOf("</row>")
  const body = endIdx >= 0 ? rowXml.slice(0, endIdx) : rowXml
  return body.split("<c ").slice(1).map((cellXml) => cellValue("<c " + cellXml, ss))
}

function cellValue(cellXml: string, ss: string[]): string | null {
  if (cellXml.includes('t="inlineStr"')) {
    const matches = [...cellXml.matchAll(/<t(?:[^>]*)>(.*?)<\/t>/gs)]
    return matches.length ? unescapeXml(matches.map((m) => m[1]).join("")) : null
  }
  if (cellXml.includes('t="s"')) {
    const m = cellXml.match(/<v>(\d+)<\/v>/)
    return m ? (ss[parseInt(m[1], 10)] ?? null) : null
  }
  const m = cellXml.match(/<v>([^<]*)<\/v>/)
  return m ? unescapeXml(m[1]) : null
}

function unescapeXml(str: string): string {
  return str
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&amp;/g, "&")
}
